/**
 * Billing for the web/desktop shell (one per app container): the BillingApi (HTTP, or the in-browser scenarios in mock
 * mode, code-split), how Stripe's pages open (this tab on the web, so Checkout and the portal come back to Settings →
 * Billing; the system browser on desktop), and where a dismissed trial banner's snooze is kept (localStorage).
 * The web and desktop sell; the native apps never do.
 */
import type { BillingApi } from "@bundle/core";
import type { BillingContextValue } from "@bundle/ui";
import type { Container } from "../container.ts";
import { isDesktop, openInBrowser } from "../desktop.ts";
import { PRO_CONTACT_URL } from "../../shell/billing.ts";
import { createBillingApi } from "./api.ts";

const byContainer = new WeakMap<Container, BillingContextValue>();

// Back from Stripe Checkout (`?checkout=success|canceled` on Settings → Billing): read when the app boots, before the
// shell's URL sync rewrites the address to the tab's route, and handed to the Billing page once.
// The address the app booted with (mock scenarios read `?billing=` from it; the shell rewrites the URL soon after).
const bootSearch = (() => {
  try {
    return location.search;
  } catch {
    return "";
  }
})();

let checkoutReturn: "success" | "canceled" | undefined = (() => {
  try {
    const v = new URLSearchParams(bootSearch).get("checkout");
    return v === "success" || v === "canceled" ? v : undefined;
  } catch {
    return undefined;
  }
})();

/** The Checkout result this page load came back with, once (later calls answer undefined). */
export function takeCheckoutReturn(): "success" | "canceled" | undefined {
  const v = checkoutReturn;
  checkoutReturn = undefined;
  return v;
}

function deferred<T extends object>(target: Promise<T>): T {
  return new Proxy({} as T, {
    get: (_obj, key) => (key === "then" ? undefined : (...args: unknown[]) => target.then((t) => (t[key as keyof T] as (...a: unknown[]) => unknown)(...args))),
  });
}

function snoozeFor(wsId: string, kind?: "storage") {
  const key = kind === undefined ? `bundle.billing.snooze.${wsId}` : `bundle.billing.snooze.${kind}.${wsId}`;
  return {
    get: (): number | undefined => {
      try {
        const v = Number(localStorage.getItem(key));
        return Number.isFinite(v) && v > 0 ? v : undefined;
      } catch {
        return undefined;
      }
    },
    set: (until: number): void => {
      try {
        localStorage.setItem(key, String(until));
      } catch {
        // private mode: memory only
      }
    },
  };
}

export function billingPlatform(container: Container): BillingContextValue {
  const existing = byContainer.get(container);
  if (existing !== undefined) return existing;
  const api: BillingApi = container.mock ? deferred<BillingApi>(import("../mock/billing.ts").then((m) => m.createMockBillingApi(bootSearch))) : createBillingApi(container.session);
  const value: BillingContextValue = {
    api,
    open: (url) => {
      if (isDesktop()) void openInBrowser(url);
      else location.assign(url);
    },
    sells: true,
    snooze: (wsId) => snoozeFor(wsId),
    storageSnooze: (wsId) => snoozeFor(wsId, "storage"),
    contactPro: () => void window.open(PRO_CONTACT_URL, "_blank", "noopener"),
    takeCheckoutReturn,
  };
  byContainer.set(container, value);
  return value;
}
